/* TCS responsive homepage controller — 2026-09-15.
 * Owns only #tcs. Desktop keeps the existing content, CSS, carousel speeds,
 * randomization and reveal behavior. Phones use finite, user-controlled rails.
 * No prices, cart operations, subscription data, or marketing consent changes.
 */
(() => {
  'use strict';
  const phone = window.matchMedia('(max-width: 749px)');
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const started = performance.now();

  // Called by the existing capture suite before its own eligibility checks.
  // A failed gate never marks a signup as shown/subscribed or locks the page.
  window.TCSMobileCaptureAllowed = (source) => {
    if (!phone.matches) return true;
    if (source === 'exit' || document.hidden || performance.now() - started < 25000) return false;
    const focused = document.activeElement;
    if (focused && focused.matches('input, textarea, select, [contenteditable="true"]')) return false;
    const height = Math.max(document.documentElement.scrollHeight, document.body.scrollHeight) - window.innerHeight;
    if (height > 0 && window.scrollY / height < 0.4) return false;
    const newsletter = document.querySelector('.tcs-footer__newsletter');
    if (newsletter) {
      const rect = newsletter.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) return false;
    }
    return !Array.from(document.querySelectorAll('[aria-modal="true"]')).some((dialog) => {
      const style = getComputedStyle(dialog);
      const hiddenAncestor = dialog.closest('[aria-hidden="true"], [inert], [hidden]');
      return !hiddenAncestor && style.display !== 'none' && style.visibility !== 'hidden' && Number(style.opacity) !== 0 && dialog.getClientRects().length > 0;
    });
  };

  let instance = null;
  function createHome(root) {
    const sections = Array.from(root.children);
    const tracks = ['tc-review-track', 'tc-article-track', 'tc-feed-track'].map((id) => {
      const node = root.querySelector('#' + id);
      return node ? { node, children: Array.from(node.children) } : null;
    }).filter(Boolean);
    const trackById = (id) => tracks.find((track) => track.node.id === id);
    let undo = [];
    let mode = null;
    const later = (fn, delay) => {
      const timer = window.setTimeout(fn, delay);
      undo.push(() => window.clearTimeout(timer));
    };
    const listen = (target, event, fn, options) => {
      target.addEventListener(event, fn, options);
      undo.push(() => target.removeEventListener(event, fn, options));
    };
    const replaceContent = (node, content) => {
      if (!node) return;
      const old = Array.from(node.childNodes);
      node.textContent = content;
      undo.push(() => node.replaceChildren(...old));
    };
    const appendTemporary = (parent, node) => {
      if (!parent) return;
      parent.appendChild(node);
      undo.push(() => node.remove());
    };
    const addClass = (node, name) => {
      if (!node) return;
      const had = node.classList.contains(name);
      node.classList.add(name);
      undo.push(() => { if (!had) node.classList.remove(name); });
    };
    const attr = (node, name, value) => {
      const old = node.getAttribute(name);
      node.setAttribute(name, value);
      undo.push(() => old === null ? node.removeAttribute(name) : node.setAttribute(name, old));
    };
    function clearMode() {
      undo.reverse().forEach((cleanup) => cleanup());
      undo = [];
      sections.forEach((section) => root.appendChild(section));
      tracks.forEach(({ node, children }) => {
        node.replaceChildren(...children);
        node.scrollLeft = 0;
        node.classList.remove('dragging');
        delete node.dataset.infinite;
      });
      root.querySelectorAll('.tc-reveal').forEach((node) => node.classList.remove('show'));
    }

    function rail(track) {
      const node = track.node;
      const prev = root.querySelector('[data-prev="' + node.id + '"]');
      const next = root.querySelector('[data-next="' + node.id + '"]');
      const update = () => {
        if (prev) prev.disabled = node.scrollLeft <= 2;
        if (next) next.disabled = node.scrollWidth - node.clientWidth - node.scrollLeft <= 2;
      };
      [prev, next].forEach((button) => {
        if (!button) return;
        const originalDisabled = button.disabled;
        undo.push(() => { button.disabled = originalDisabled; });
        listen(button, 'click', () => {
          const first = node.firstElementChild;
          const step = first ? first.getBoundingClientRect().width + 12 : node.clientWidth * .82;
          node.scrollBy({ left: button === prev ? -step : step, behavior: reduced.matches ? 'auto' : 'smooth' });
        });
      });
      listen(node, 'scroll', update, { passive: true });
      listen(window, 'resize', update, { passive: true });
      attr(node, 'role', 'region');
      attr(node, 'aria-label', node.id === 'tc-review-track' ? 'Customer notes. Swipe or use the arrow buttons.' : 'Latest articles. Swipe or use the arrow buttons.');
      later(update, 0);
      return update;
    }

    function mobile() {
      const hero = root.querySelector('.tc-hero');
      const shop = root.querySelector('.tc-shop');
      const trust = root.querySelector('.tc-trust');
      const science = root.querySelector('.tc-science');
      const feature = root.querySelector('.tc-feature');
      const base = root.querySelector('.tc-base');
      const defined = root.querySelector('.tc-defined');
      const reviews = trackById('tc-review-track');
      const articles = trackById('tc-article-track');
      const feed = trackById('tc-feed-track');
      const reviewSection = reviews && reviews.node.closest('section');
      const articleSection = articles && articles.node.closest('section');
      const feedSection = feed && feed.node.closest('section');
      const final = root.querySelector('.tc-final');
      addClass(reviewSection, 'tc-mobile-reviews');
      addClass(articleSection, 'tc-mobile-articles');
      addClass(feedSection, 'tc-mobile-feed');
      // Reorder the actual DOM as well as CSS: touch, keyboard and reading order agree.
      [hero, shop, trust, science, feature, reviewSection, base, defined, articleSection, feedSection, final].filter(Boolean).forEach((section) => root.appendChild(section));
      replaceContent(hero && hero.querySelector('.tc-hero-grid > div > p'), 'Science-led nutrition for metabolism, muscle, sleep and measurable progress.');
      replaceContent(hero && hero.querySelector('.tc-actions > a:first-child'), 'Shop the system →');
      replaceContent(shop && shop.querySelector('.tc-shop-head h2'), 'Three foundations. One system.');
      const cardCopy = {
        core: 'Daily nutritional support for healthy glucose metabolism.',
        protein: 'Whey isolate, creatine, BCAAs and vitamin D3 in one daily formula.',
        sleep: 'A nighttime formula for sleep and recovery.'
      };
      Object.keys(cardCopy).forEach((kind) => {
        const card = shop && shop.querySelector('.tc-card.' + kind);
        replaceContent(card && card.querySelector('.tc-card-info > p'), cardCopy[kind]);
        replaceContent(card && card.querySelector('.tc-card-foot > span:last-child'), kind === 'protein' ? 'Choose flavor →' : 'Shop →');
      });
      if (shop) {
        const hint = document.createElement('p');
        hint.className = 'tc-mobile-hint';
        hint.textContent = 'Swipe to explore Core, Protein and Sleep →';
        appendTemporary(shop.querySelector('.tc-w'), hint);
        const grid = shop.querySelector('.tc-grid');
        if (grid) { attr(grid, 'role', 'region'); attr(grid, 'aria-label', 'The Take Control product system'); }
      }
      const manifesto = defined && defined.querySelector('.tc-manifesto');
      if (manifesto) {
        const parent = manifesto.parentNode;
        const anchor = document.createComment('TCS mobile disclosure anchor');
        parent.insertBefore(anchor, manifesto);
        const details = document.createElement('details');
        details.className = 'tc-mobile-defined-details';
        const summary = document.createElement('summary');
        summary.textContent = 'What Take Control means';
        details.append(summary, manifesto);
        parent.insertBefore(details, anchor);
        undo.push(() => { parent.insertBefore(manifesto, anchor); details.remove(); anchor.remove(); });
      }
      if (reviews) {
        reviews.node.replaceChildren(...reviews.children.slice(0, 5));
        const update = rail(reviews);
        if (reviews.children.length > 5) {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'tc-mobile-more';
          button.textContent = 'More customer notes →';
          button.setAttribute('aria-controls', reviews.node.id);
          button.setAttribute('aria-expanded', 'false');
          listen(button, 'click', () => {
            const expand = button.getAttribute('aria-expanded') !== 'true';
            reviews.node.replaceChildren(...(expand ? reviews.children : reviews.children.slice(0, 5)));
            button.setAttribute('aria-expanded', String(expand));
            button.textContent = expand ? 'Show fewer customer notes' : 'More customer notes →';
            reviews.node.scrollLeft = expand ? Math.max(0, (reviews.node.firstElementChild.getBoundingClientRect().width + 12) * 5) : 0;
            update();
          });
          appendTemporary(reviewSection, button);
        }
      }
      if (articles) {
        // Original Liquid already sorts by publication date. Do not shuffle mobile.
        articles.node.replaceChildren(...articles.children.slice(0, 3));
        rail(articles);
        const link = document.createElement('a');
        link.href = '/blogs/field-notes';
        link.className = 'tc-mobile-journal-link';
        link.textContent = 'Read all Field Notes →';
        appendTemporary(articleSection, link);
      }
      // Detach hidden feed cards, preventing their lazy images and duplicate slides.
      if (feed) feed.node.replaceChildren();
      root.querySelectorAll('.tc-reveal').forEach((node) => node.classList.add('show'));
    }

    // Original desktop interaction model, now with teardown on breakpoint changes.
    function infinite(track, speed) {
      const node = track.node;
      const originals = track.children.slice();
      if (!originals.length) return;
      for (let i = originals.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [originals[i], originals[j]] = [originals[j], originals[i]];
      }
      const sequence = originals.map((item) => item.cloneNode(true));
      node.replaceChildren();
      for (let copy = 0; copy < 3; copy++) {
        sequence.forEach((item) => {
          const clone = item.cloneNode(true);
          if (copy !== 1) {
            clone.setAttribute('aria-hidden', 'true');
            if (clone.matches('a, button, input, [tabindex]')) clone.tabIndex = -1;
            clone.querySelectorAll('a, button, input, [tabindex]').forEach((child) => { child.tabIndex = -1; });
          }
          node.appendChild(clone);
        });
      }
      node.dataset.infinite = '1';
      let segment = 0, paused = false, dragging = false, startX = 0, startScroll = 0, lastTime = 0, frame = 0;
      const normalize = () => {
        if (!segment) return;
        if (node.scrollLeft < segment * .45) node.scrollLeft += segment;
        if (node.scrollLeft > segment * 1.55) node.scrollLeft -= segment;
      };
      const measure = () => { segment = node.scrollWidth / 3; if (segment > 0) node.scrollLeft = segment; };
      later(measure, 0);
      listen(window, 'resize', () => {
        const ratio = segment ? node.scrollLeft / segment : 1;
        segment = node.scrollWidth / 3;
        if (segment) node.scrollLeft = segment * (ratio > 1.5 ? 1 : ratio);
      });
      const animate = (time) => {
        if (!lastTime) lastTime = time;
        const delta = time - lastTime;
        lastTime = time;
        if (!paused && !dragging && !reduced.matches && !document.hidden && delta < 80) {
          node.scrollLeft += speed * (delta / 16.67);
          normalize();
        }
        frame = requestAnimationFrame(animate);
      };
      frame = requestAnimationFrame(animate);
      undo.push(() => cancelAnimationFrame(frame));
      listen(node, 'mouseenter', () => { paused = true; });
      listen(node, 'mouseleave', () => { paused = false; dragging = false; node.classList.remove('dragging'); });
      listen(node, 'mousedown', (event) => { dragging = true; paused = true; startX = event.pageX; startScroll = node.scrollLeft; node.classList.add('dragging'); });
      listen(window, 'mouseup', () => { if (!dragging) return; dragging = false; paused = false; node.classList.remove('dragging'); normalize(); });
      listen(node, 'mousemove', (event) => { if (!dragging) return; event.preventDefault(); node.scrollLeft = startScroll - (event.pageX - startX) * 1.12; normalize(); });
      listen(node, 'touchstart', () => { paused = true; }, { passive: true });
      listen(node, 'touchend', () => later(() => { normalize(); paused = false; }, 900), { passive: true });
      ['prev', 'next'].forEach((direction) => {
        root.querySelectorAll('[data-' + direction + '="' + node.id + '"]').forEach((button) => {
          listen(button, 'click', () => {
            paused = true;
            node.scrollBy({ left: (direction === 'prev' ? -1 : 1) * Math.max(300, node.clientWidth * .65), behavior: reduced.matches ? 'auto' : 'smooth' });
            later(() => { normalize(); paused = false; }, 850);
          });
        });
      });
    }
    function desktop() {
      const reveal = root.querySelectorAll('.tc-reveal');
      if ('IntersectionObserver' in window && !reduced.matches) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => { if (entry.isIntersecting) { entry.target.classList.add('show'); observer.unobserve(entry.target); } });
        }, { threshold: .07 });
        reveal.forEach((node) => observer.observe(node));
        undo.push(() => observer.disconnect());
      } else reveal.forEach((node) => node.classList.add('show'));
      tracks.forEach((track) => infinite(track, track.node.id === 'tc-review-track' ? .30 : track.node.id === 'tc-article-track' ? .24 : .20));
    }
    function update() {
      const next = phone.matches ? 'mobile' : 'desktop';
      if (mode === next) return;
      if (mode !== null) clearMode();
      mode = next;
      root.dataset.tcsResponsiveMode = next;
      if (next === 'mobile') mobile(); else desktop();
    }
    update();
    phone.addEventListener('change', update);
    return { root, destroy() { phone.removeEventListener('change', update); clearMode(); delete root.dataset.tcsResponsiveMode; } };
  }
  function init() {
    const root = document.getElementById('tcs');
    if (instance && instance.root === root) return;
    if (instance) instance.destroy();
    instance = root ? createHome(root) : null;
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', (event) => {
    if (instance && event.target.contains(instance.root)) { instance.destroy(); instance = null; }
  });
})();
